import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowRight, BookOpenCheck, ShieldAlert, Users2 } from "lucide-react";

const PHASES = [
  {
    label: "Phase 0 · One-time setup",
    who: "Super admin only",
    steps: [
      ["Bank Balances", "/banks", "Add every bank account with its opening balance as on the go-live date."],
      ["Vendors & Debtors", "/vendors", "Create the party masters. Spell names the way they appear in bank narrations — that is what drives auto-matching."],
      ["Registers", "/fixed-deposits", "Import the existing FD register, then Fixed Assets and Investments."],
    ],
  },
  {
    label: "Phase 1 · Every day, in this order",
    who: "Accountant (Finance)",
    steps: [
      ["1. Book accruals first", "/payables", "Vendor bills → Payables → Upload invoice. Client invoices → Receivables → Upload client invoice. This books the expense/revenue and the liability/asset together. Manual add only when there is no PDF."],
      ["2. Upload the bank statement", "/banks", "One account at a time, always including the page that shows the closing balance. Nothing hits the P&L here — lines are only staged."],
      ["3. Match every line", "/recon", "Confirm the invoice each payment settles. No invoice behind it? Book it directly with a proper head. Internal movement → transfer; sweep in/out → sweep; FD booked/matured → FD; duplicate → ignore."],
      ["4. Tie out sweep & FD", "/sweeps", "Enter the sweep balance as per the bank. The statement must read Tallied."],
      ["5. Sign off the day", "/day-close", "All blocking checkpoints green → write the note → Close & lock. Supervision signs off; after that only a super admin can reopen."],
    ],
  },
  {
    label: "Phase 2 · Next morning",
    who: "Supervision",
    steps: [
      ["Clear the Exception Inbox", "/inbox", "Unmatched lines, overdue invoices, days not signed off — clear anything red before starting the new day."],
    ],
  },
  {
    label: "Phase 3 · Monthly, by the 3rd working day",
    who: "Super admin",
    steps: [
      ["FIRC, assets, investments", "/investments", "Record FIRCs against export invoices, add new fixed assets and investments."],
      ["Prove the books", "/journal", "Trial Balance must show Dr = Cr with zero unbalanced vouchers."],
      ["Publish & archive", "/mis", "Review the MIS, then Backup & Reset → Export Everything + Backup Now."],
    ],
  },
];

const RULES = [
  "One event, one door. Bills enter only through Payables; cash movement only through the bank statement.",
  "The Expenses and Income tabs are for viewing and correcting — never for entering a bill that has an invoice.",
  "Settle invoices only from Bank Reconciliation, so cash and books move together.",
  "Never hand-edit a bank balance. The statement's closing balance decides it (super admin override is logged).",
  "One person uploads statements per account per day.",
  "To fix a wrong match: Unpost → correct → re-match. Never delete the accrual.",
  "\u201cOther\u201d as an expense head is a to-do, not an answer.",
  "Reset and Restore need the super admin, and always a fresh backup first.",
];

const ROLES = [
  ["Finance (accountant)", "Books accruals, uploads statements, matches lines, books direct entries. Cannot close a day, edit bank masters, reverse posted entries or reset the workspace."],
  ["Supervision", "Everything Finance can do, plus signing off and locking the day, and reversing posted entries."],
  ["Super admin", "Everything, plus bank masters, balance overrides, reopening a closed day, backups and resets."],
];

export default function Sop() {
  const navigate = useNavigate();

  return (
    <div className="space-y-6 max-w-5xl" data-testid="sop-page">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight">How to use this system</h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
          The order below is what keeps the books double-entry clean. Each event has exactly one
          entry point — the product now blocks the duplicate doors, so following this is the fast
          path, not just the correct one.
        </p>
      </div>

      {PHASES.map((phase) => (
        <Card key={phase.label} className="p-5 space-y-4" data-testid={`sop-phase-${phase.label.slice(0, 7)}`}>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="font-heading text-sm tracking-tight">{phase.label}</p>
            <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border border-brand/30 bg-brand/10 text-brand">
              {phase.who}
            </span>
          </div>
          <div className="divide-y divide-white/[0.04]">
            {phase.steps.map(([title, route, detail]) => (
              <div key={title} className="py-3 flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-sm font-medium">{title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{detail}</p>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => navigate(route)}
                  className="gap-1.5 shrink-0 text-muted-foreground hover:text-foreground"
                  data-testid={`sop-go-${route.replace("/", "") || "home"}`}
                >
                  Open
                  <ArrowRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        </Card>
      ))}

      <Card className="p-5 space-y-3" data-testid="sop-rules">
        <div className="flex items-center gap-2">
          <ShieldAlert className="h-4 w-4 text-amber-400" />
          <p className="font-heading text-sm tracking-tight">Golden rules</p>
        </div>
        <ol className="space-y-2">
          {RULES.map((r, i) => (
            <li key={i} className="flex gap-2.5 text-sm">
              <span className="font-mono text-xs text-brand mt-0.5">{String(i + 1).padStart(2, "0")}</span>
              <span className="text-muted-foreground">{r}</span>
            </li>
          ))}
        </ol>
      </Card>

      <Card className="p-5 space-y-3" data-testid="sop-roles">
        <div className="flex items-center gap-2">
          <Users2 className="h-4 w-4 text-brand" />
          <p className="font-heading text-sm tracking-tight">Who can do what</p>
        </div>
        <div className="divide-y divide-white/[0.04]">
          {ROLES.map(([role, detail]) => (
            <div key={role} className="py-3">
              <p className="text-sm font-medium">{role}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{detail}</p>
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-5 flex items-start gap-3" data-testid="sop-footer">
        <BookOpenCheck className="h-4 w-4 text-brand mt-0.5 shrink-0" />
        <p className="text-xs text-muted-foreground">
          A closed day is final: entries dated inside it are rejected for everyone except the super
          admin, who can reopen it from Day-End Control. That is what makes the signed-off numbers
          trustworthy for investors.
        </p>
      </Card>
    </div>
  );
}
